"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { format } from "date-fns";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { currentLanguageLocale } from "@/i18n/getDateFnsLocale";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { toast } from "@/utils/customToast";

// helper for colored status dot
function getStatusDotClass(status?: string | null): string {
  const s = (status || "").toLowerCase();

  if (s.includes("accept") || s.includes("confirm")) {
    return "bg-emerald-400";
  } else if (s.includes("reject") || s.includes("declin") || s.includes("fail")) {
    return "bg-red-500";
  } else if (s.includes("submit") || s.includes("pend")) {
    return "bg-amber-400";
  }

  return "bg-sky-400";
}

export interface PaymentConfirmation {
  id: string;
  evidence_url?: string | null;
  description?: string | null;
  status: string;
  review_comment?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

type Props = {
  paymentId: string | null;
  confirmations?: PaymentConfirmation[] | null;
  initialConfirmationId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolved: () => Promise<void> | void;
  allowResolveActions?: boolean;
};

export default function ConfirmationReviewDialog({
  paymentId,
  confirmations,
  initialConfirmationId,
  open,
  onOpenChange,
  onResolved,
  allowResolveActions = true,
}: Props) {
  const { t } = useTranslation(["finances", "common"]);
  const [view, setView] = useState<"list" | "detail">("list");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // If there's only one confirmation, automatically open it
      if (confirmations && confirmations.length === 1) {
        setView("detail");
        setSelectedId(confirmations[0].id);
      } else {
        setView("list");
        setSelectedId(null);
      }
    }
  }, [open, confirmations]);

  const selectedConfirmation = useMemo(() => {
    if (!confirmations || confirmations.length === 0) return null;
    if (selectedId) return confirmations.find((c) => c.id === selectedId) || null;

    return null;
  }, [confirmations, selectedId]);

  // Sort confirmations to prioritize "submitted" status first
  const sortedConfirmations = useMemo(() => {
    if (!confirmations) return [];

    return [...confirmations].sort((a, b) => {
      const aStatus = a.status.toLowerCase();
      const bStatus = b.status.toLowerCase();

      // "submitted" status gets priority
      if (aStatus.includes("submit") && !bStatus.includes("submit")) return -1;
      if (!aStatus.includes("submit") && bStatus.includes("submit")) return 1;

      // Keep original order for same status
      return 0;
    });
  }, [confirmations]);

  const canResolve = Boolean(paymentId) && Boolean(selectedConfirmation) && allowResolveActions;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
      }}
    >
      <DialogContent className="bg-background/80 h-fit max-h-[90dvh] max-w-2xl border-none p-0 backdrop-blur-xl">
        <div className="flex h-full flex-col">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="px-6 pt-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="pb-4">
              <DialogTitle className="text-accent-foreground flex items-center justify-between gap-3 text-xl font-semibold">
                <div className="flex items-center justify-start gap-3 text-start">
                  <div className="bg-muted/20 flex items-center rounded-xl p-2 backdrop-blur-sm">
                    <Icon
                      className="text-accent-foreground"
                      height="1.25rem"
                      icon="mdi:clock-outline"
                      width="1.25rem"
                    />
                  </div>
                  {view === "list"
                    ? t("finances:paymentsDetails.confirmations.listTitle", "Payment confirmations")
                    : t(
                        "finances:paymentsDetails.confirmations.dialogTitle",
                        "Review confirmation",
                      )}
                </div>
                {view === "detail" && confirmations?.length && confirmations?.length > 1 && (
                  <Button
                    className="me-4 h-8 gap-2 px-3 text-xs"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setView("list");
                      setSelectedId(null);
                    }}
                  >
                    <Icon height="1rem" icon="mdi:arrow-left" width="1rem" />
                    {t("common:back", "Back")}
                  </Button>
                )}
              </DialogTitle>
            </DialogHeader>
          </motion.div>

          <AnimatePresence initial={false} mode="wait">
            {view === "list" && (
              <motion.div
                key="list"
                layout
                animate={{ opacity: 1, y: 0 }}
                className="flex min-h-0 flex-1 flex-col px-6 pb-6"
                exit={{ opacity: 0, y: -8 }}
                initial={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2 }}
              >
                {(!confirmations || confirmations.length === 0) && (
                  <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                    {t(
                      "finances:paymentsDetails.confirmations.empty",
                      "No confirmations submitted yet",
                    )}
                  </div>
                )}
                {confirmations && confirmations.length > 0 && (
                  <ScrollArea className="max-h-[60dvh]">
                    <div className="grid grid-cols-1 gap-3 pr-4">
                      {sortedConfirmations.map((c) => {
                        const dotClass = getStatusDotClass(c.status);

                        return (
                          <button
                            key={c.id}
                            className={`bg-foreground/50 hover:bg-foreground/60 group relative flex w-full items-stretch gap-3 rounded-xl p-3 text-left transition-colors ${
                              c.id === initialConfirmationId ? "ring-muted/50 ring-1" : ""
                            }`}
                            type="button"
                            onClick={() => {
                              setSelectedId(c.id);
                              setView("detail");
                            }}
                          >
                            <div className="bg-muted/20 flex size-12 shrink-0 items-center justify-center rounded-lg">
                              <Icon
                                className="text-accent-foreground"
                                height="1.25rem"
                                icon="mdi:file-eye"
                                width="1.25rem"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 max-sm:mb-2 max-sm:flex-col max-sm:items-start max-sm:gap-0">
                                <span className="text-accent-foreground inline-flex items-center gap-1 text-sm font-medium capitalize">
                                  <span
                                    className={`inline-block h-2 w-2 rounded-full ${dotClass}`}
                                  />
                                  {c.status}
                                </span>
                                <span className="text-muted-foreground text-xs">
                                  {c.created_at
                                    ? format(new Date(c.created_at), "PPPp", {
                                        locale: currentLanguageLocale(),
                                      })
                                    : ""}
                                </span>
                              </div>
                              {c.description && (
                                <div className="text-muted-foreground mt-0.5 line-clamp-1 max-w-full flex-1 truncate text-xs">
                                  {c.description}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center">
                              <Icon
                                className="text-muted-foreground transition-transform group-hover:translate-x-0.5"
                                height="1rem"
                                icon="mdi:chevron-right"
                                width="1rem"
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </motion.div>
            )}

            {view === "detail" && selectedConfirmation && (
              <motion.div
                key="detail"
                layout
                animate={{ opacity: 1, y: 0 }}
                className="px-6 pb-6"
                exit={{ opacity: 0, y: -8 }}
                initial={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2 }}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-muted-foreground text-sm">
                      {t("finances:paymentsDetails.confirmations.status", "Status")}
                    </div>
                    <div className="text-accent-foreground inline-flex items-center gap-2 font-medium">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${getStatusDotClass(
                          selectedConfirmation.status,
                        )}`}
                      />
                      {selectedConfirmation.status}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-muted-foreground text-sm">
                      {t("common:createdAt", "Created at")}
                    </div>
                    <div className="text-accent-foreground font-medium">
                      {selectedConfirmation.created_at
                        ? format(new Date(selectedConfirmation.created_at), "PPPp", {
                            locale: currentLanguageLocale(),
                          })
                        : ""}
                    </div>
                  </div>
                </div>
                {selectedConfirmation.description && (
                  <div>
                    <div className="text-accent-foreground mb-1 text-sm font-semibold">
                      {t("common:description", "Description")}
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {selectedConfirmation.description}
                    </div>
                  </div>
                )}
                {selectedConfirmation.evidence_url && (
                  <div>
                    <div className="text-accent-foreground mb-2 text-sm font-semibold">
                      {t("finances:paymentsDetails.confirmations.evidence", "Evidence")}
                    </div>
                    <img
                      alt={t(
                        "finances:paymentsDetails.confirmations.evidenceAlt",
                        "Evidence image",
                      )}
                      className="h-100 w-full rounded-xl object-contain"
                      src={selectedConfirmation.evidence_url}
                    />
                  </div>
                )}
                `
                {selectedConfirmation.review_comment && (
                  <div>
                    <div className="text-accent-foreground mb-1 text-sm font-semibold">
                      {t("finances:paymentsDetails.confirmations.reviewComment", "Review comment")}
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {selectedConfirmation.review_comment}
                    </div>
                  </div>
                )}
                {canResolve && (
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                      className="w-full gap-2 sm:w-auto"
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        if (!paymentId) return;
                        try {
                          await axiosApiInstance.post(
                            API_ENDPOINTS.TUTOR.FINANCES.PAYMENT_REJECT(paymentId),
                          );
                          toast.success(
                            t("finances:paymentsDetails.actions.rejectSuccess", "Payment rejected"),
                          );
                          await onResolved?.();
                          onOpenChange(false);
                        } catch (err: any) {
                          toast.error(
                            err?.response?.data?.message ||
                              t(
                                "finances:paymentsDetails.actions.rejectError",
                                "Failed to reject payment",
                              ),
                          );
                        }
                      }}
                    >
                      <Icon height="1rem" icon="mdi:close-circle" width="1rem" />
                      {t("finances:paymentsDetails.actions.rejectShort", "Reject")}
                    </Button>
                    <Button
                      className="w-full gap-2 sm:w-auto"
                      size="sm"
                      variant="foreground"
                      onClick={async () => {
                        if (!paymentId) return;
                        try {
                          await axiosApiInstance.post(
                            API_ENDPOINTS.TUTOR.FINANCES.PAYMENT_CONFIRM(paymentId),
                          );
                          toast.success(
                            t("finances:paymentsDetails.actions.acceptSuccess", "Payment accepted"),
                          );
                          await onResolved?.();
                          onOpenChange(false);
                        } catch (err: any) {
                          toast.error(
                            err?.response?.data?.message ||
                              t(
                                "finances:paymentsDetails.actions.acceptError",
                                "Failed to accept payment",
                              ),
                          );
                        }
                      }}
                    >
                      <Icon height="1rem" icon="mdi:check-circle" width="1rem" />
                      {t("finances:paymentsDetails.actions.acceptShort", "Accept")}
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
